import type { Row, Table } from '@tanstack/react-table'
import * as React from 'react'

import type { SelectOption } from '@/components/ui/filters/FilterDialog/inputs/types'
import { useFilters } from '../context'
import type { FieldSchema, Filter, Tag } from '../types'

interface UseTableFiltersOptions<T> {
  table: Table<T> | null
  schema: FieldSchema[]
}

interface UseTableFiltersReturn {
  filters: Filter[]
  getUniqueValues: (field: string) => SelectOption[]
}

const getSelectFieldValues =
  <T>(fieldSchema: FieldSchema) =>
  (values: Set<string>, row: Row<T>) => {
    const record = row.original as Record<string, null | undefined | string>
    const value = record[fieldSchema.field]

    if (value?.trim() && value !== 'Null') {
      values.add(value as string)
    }
    return values
  }

const getTagsFieldValues =
  <T>(fieldSchema: FieldSchema) =>
  (tags: Set<string>, row: Row<T>) => {
    const record = row.original as Record<string, Tag[]>
    const list = record[fieldSchema.field]

    list.forEach((tag: Tag) => tags.add(tag.name))
    return tags
  }

export const useTableFilters = <T>({
  table,
  schema,
}: UseTableFiltersOptions<T>): UseTableFiltersReturn => {
  const filters = useFilters()

  const uniqueValuesMap = React.useMemo(() => {
    if (!table) return {}

    const rows = table.getCoreRowModel().rows
    const map: Record<string, unknown[]> = {}

    schema.forEach((fieldSchema) => {
      if (!['select', 'tags'].includes(fieldSchema.type)) return

      const getValues =
        fieldSchema.type === 'tags'
          ? getTagsFieldValues<T>(fieldSchema)
          : getSelectFieldValues<T>(fieldSchema)

      const values = rows.reduce(getValues, new Set<string>())
      map[fieldSchema.field] = Array.from(values).sort()
    })

    return map
  }, [table, schema])

  const getUniqueValues = (field: string): SelectOption[] =>
    (uniqueValuesMap[field] || []) as SelectOption[]

  return {
    filters,
    getUniqueValues,
  }
}
